// contexts/PageViewContext.tsx
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import router from 'next/router'

interface PageViewState {
  dayBeginning: number
  pageViewedInDay: number
}

interface PageViewContextType {
  pageViewInDayCount?: number | null
}

const LOCAL_STORAGE_KEY = '@app/daily-page-views'
const NEW_DAY_TIMEOUT = 86400000 // 24 hours in milliseconds

const PageViewInDayContext = createContext<PageViewContextType>({
  pageViewInDayCount: null,
})

export const PageViewInDayProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [pageView, setPageView] = useState<PageViewState | null>(null)

  const parseDayViewData = (): PageViewState => {
    const timestamp = Date.now()
    const storedData = localStorage.getItem(LOCAL_STORAGE_KEY)

    if (!storedData) {
      return { dayBeginning: timestamp, pageViewedInDay: 1 }
    }

    const [storedTimestamp, storedViews] = storedData
      .split(',')
      .map(i => parseInt(i, 10))

    if (timestamp - storedTimestamp > NEW_DAY_TIMEOUT) {
      return { dayBeginning: timestamp, pageViewedInDay: 1 }
    }

    return {
      dayBeginning: storedTimestamp,
      pageViewedInDay: storedViews + 1,
    }
  }

  useEffect(() => {
    const currentState = parseDayViewData()
    setPageView(currentState)
    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      `${currentState.dayBeginning},${currentState.pageViewedInDay}`
    )
  }, [])

  useEffect(() => {
    const handleRouteChange = (destination: string) => {
      const origin = router.asPath
      if (
        !pageView ||
        (destination.includes('allshows') && !origin.includes('allshows')) ||
        (origin.includes('allshows') && !destination.includes('allshows'))
      ) {
        return
      }

      const updatedState = {
        ...pageView,
        pageViewedInDay: pageView.pageViewedInDay + 1,
      }

      localStorage.setItem(
        LOCAL_STORAGE_KEY,
        `${updatedState.dayBeginning},${updatedState.pageViewedInDay}`
      )
      setPageView(updatedState)
    }

    router.events.on('routeChangeStart', handleRouteChange)
    return () => router.events.off('routeChangeStart', handleRouteChange)
  }, [pageView])

  // useEffect(() => {
  //   prettyLog('usePageViewInDay: 73', { pageView: pageView?.pageViewedInDay })
  // }, [pageView?.pageViewedInDay])

  return (
    <PageViewInDayContext.Provider
      value={{ pageViewInDayCount: pageView?.pageViewedInDay }}
    >
      {children}
    </PageViewInDayContext.Provider>
  )
}

export const usePageViewInDay = () => {
  return useContext(PageViewInDayContext)
}
